import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Overlay,
  ModalContainer,
  ModalContent,
  Title,
  Subtitle,
  Label,
  PasswordWrapper,
  GenerateButton,
  ButtonGroup,
  CancelButton,
  ShareButton,
  PasswordRow,
  PasswordInput,
  BackButton,
  SpanTitle,
  StyledDatePicker,
  PasswordIcons,
  HorizondalLine,
  AccessListSection,
  RevokeLink,
  SubLabel,
  ErrorText,
  ErrorIconWrapper,
  FolderPicker,
  FolderRow,
  PickerNote,
  LinkPanel,
  LinkRow,
  LinkMeta,
  CopyRow,
  LinkInput,
  CopyLinkButton,
} from "./ShareFileModalStyled";
import { ArrowsClockwise, Copy, Eye, EyeSlash, X } from "@phosphor-icons/react";
import { DatePickerDropdownStyles } from "../DatePicker";
import dayjs from "dayjs";
import { dateFormatList } from "../../utils/constants";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import { useDispatch, useSelector } from "react-redux";
import { getGeneratedPassword } from "../../slice/MatterDetailsSlice";
import {
  fetchFolderExternalShareDetails,
  revokeFolderExternalShare,
  resetFolderShareDetails,
  submitFolderExternalShare,
} from "../../slice/ExternalFolderShareSlice";
import { ErrorIcon, WarningIcon } from "../../assets/icons";
import { validateExternalShare } from "../../utils/ESignModalValidation";
import Loader from "../Loader/Loader";
import { MessageModal } from "../common/Modals";
import {
  buildFolderRows,
  foldersToSend,
  impliedByAncestor,
} from "./folderPickerRows";

// Fork of ShareFileModal.js for sharing folders (and their live subtrees)
// instead of a single document. The link this creates is LIVE: the
// recipient always sees whatever is currently in the folders, not a
// snapshot — so, unlike the document share, there is nothing here to
// "protect" ahead of time. A password is an access gate on the landing
// page, not per-file encryption; see documentation/features/external_sharing.md.
//
// Matthew, 9/9, asked this screen for several folders on one link and the
// link on screen rather than only in an email. Matthew, 10/30: the email
// goes altogether — "just have a 'create link' and give an easy copy/paste
// button. Users can send their own email." So the screen makes a link and
// hands it over; nothing is sent from here.
//
// It opens from two places: a folder (right-click in the tree, or a My
// Files row), which pre-ticks itself, and the top of a client's file (the
// matter's own "Share externally" button), which starts with nothing
// ticked and lists every link on the file whichever folder it was made
// from.

// Pre-filled so the button is live the moment the screen opens — the
// old blank date left it greyed out with nothing saying why. The default
// is the cap itself: Matthew, 10/30, "give them default 90 days on the
// link" — a doctor's opinion routinely takes longer than a month, and a
// link that dies mid-opinion means the request starts again. Safe at the
// cap because the server measures its 90 days from the UTC date and every
// user here is behind UTC, so a local date can never run a day ahead.
const MAX_EXPIRY_DAYS = 90;
export const DEFAULT_EXPIRY_DAYS = MAX_EXPIRY_DAYS;

const defaultExpiry = () => dayjs().add(DEFAULT_EXPIRY_DAYS, "day");

const ShareFolderModal = ({
  isOpen,
  onClose,
  folder_id,
  matter_id,
  title,
  handleSnackbar,
}) => {
  const [password, setPassword] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [createdLink, setCreatedLink] = useState(null);
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    expiryDate: "",
  });
  const [isLoading, setisLoading] = useState(false);

  const dispatch = useDispatch();
  const { folderShareDetails } = useSelector((state) => state.externalFolderShare);
  const isExternalShareEnabled = useSelector(
    (state) => state.featureFlags.isExternalShareEnabled
  );

  // What the screen was opened from. A file wins when both are given.
  const fromFile = Boolean(matter_id);
  const anchor = fromFile ? { matter_id } : folder_id ? { folder_id } : null;

  const rows = useMemo(
    () => buildFolderRows(folderShareDetails?.pickable_folders),
    [folderShareDetails?.pickable_folders]
  );
  const implied = useMemo(
    () => impliedByAncestor(rows, selectedIds),
    [rows, selectedIds]
  );
  const liveLinks = folderShareDetails?.links || [];

  // Closing does not unmount this dialog (`if (!isOpen) return null`),
  // so Done/Cancel would otherwise leave the last URL and extra ticks
  // sitting there for the next folder you share.
  useEffect(() => {
    setCreatedLink(null);
    setPassword("");
    setExpiryDate(defaultExpiry());
    setShowPassword(false);
    setShowRevokeModal(false);
    setErrors({ email: "", password: "", expiryDate: "" });
    setSelectedIds(
      !fromFile && folder_id ? new Set([folder_id]) : new Set()
    );
    if (isOpen && anchor) {
      dispatch(fetchFolderExternalShareDetails(anchor)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, folder_id, matter_id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetFolderShareDetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshLinks = () => {
    if (!anchor) return;
    dispatch(fetchFolderExternalShareDetails(anchor)).catch(() => {});
  };

  const generatePassword = () => {
    dispatch(getGeneratedPassword())
      .then((response) => {
        setPassword(response?.payload?.data?.password);
      })
      .catch(() => {});
  };

  const toggleFolder = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const sending = foldersToSend(rows, selectedIds);

  const handleSubmit = () => {
    const { externalShareErrors, hasError } = validateExternalShare({
      // Nobody is emailed from here any more; the link is the delivery.
      email: "",
      password,
      expiryDate,
      accessList: [],
      emailOptional: true,
    });

    setErrors(externalShareErrors);

    if (hasError) return;
    setisLoading(true);

    const payload = {
      folders: sending,
      password,
      expires_at: dayjs(expiryDate).format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };

    dispatch(submitFolderExternalShare(payload))
      .unwrap()
      .then((response) => {
        // The dialog stays open holding the link: the link is the
        // delivery, and closing would lose it.
        setCreatedLink(response?.data?.share_link || null);
        handleSnackbar("Link created", true, false);
        setPassword("");
        setExpiryDate(defaultExpiry());
        refreshLinks();
      })
      .catch(() => {
        handleSnackbar("Something went wrong", false, false);
      })
      .finally(() => {
        setisLoading(false);
      });
  };

  if (!isOpen) return null;

  const copyToClipboard = (value, label) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        handleSnackbar(`${label} copied to clipboard`, true);
      })
      .catch(() => {
        handleSnackbar(`Failed to copy ${label.toLowerCase()}`);
      });
  };

  const handleRevoke = () => {
    dispatch(revokeFolderExternalShare(anchor))
      .then((response) => {
        if (response?.payload?.status === 200) {
          handleSnackbar(response?.payload?.data?.message, true, false);
          setShowRevokeModal(false);
          onClose();
        } else {
          handleSnackbar("Failed to revoke share", false, false);
        }
      })
      .catch(() => {
        handleSnackbar("Something went wrong", false, false);
      });
  };

  // Links made before 10/30 may have been emailed from here; say so, since
  // that is who holds them. Newer ones were only ever copied.
  const describeLink = (link) => {
    if (link.recipient_list?.length) {
      return `Emailed to ${link.recipient_list.join(", ")}`;
    }
    return link.shared_by_name
      ? `Link only — made by ${link.shared_by_name}`
      : "Link only";
  };

  const heading = fromFile ? title : folderShareDetails?.foldername;

  return (
    <Overlay>
      {isExternalShareEnabled ? (
        <ModalContainer>
          <ModalContent>
            <Title>
              Share externally{heading ? " — " : ""}
              <SpanTitle>{heading}</SpanTitle>
            </Title>
            <Subtitle>
              Create a link and send it however you like. People with the
              link can view and download everything currently in the folders
              you tick, including subfolders. New files show up
              automatically; deleted files disappear.
            </Subtitle>

            {createdLink && (
              <LinkPanel>
                <Label small>
                  Your link is ready — copy it into your own email or message
                </Label>
                <CopyRow>
                  <LinkInput
                    readOnly
                    value={createdLink}
                    aria-label="Share link"
                    onFocus={(e) => e.target.select()}
                  />
                  <CopyLinkButton
                    type="button"
                    onClick={() => copyToClipboard(createdLink, "Link")}
                  >
                    <Copy size={16} color="#ffffff" />
                    Copy link
                  </CopyLinkButton>
                </CopyRow>
              </LinkPanel>
            )}

            <SubLabel>Which folders?</SubLabel>
            <FolderPicker>
              {rows.map((row) => {
                const isImplied = implied.has(row.id);
                return (
                  <FolderRow key={row.id} depth={row.depth}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id) || isImplied}
                      disabled={isImplied}
                      onChange={() => toggleFolder(row.id)}
                      aria-label={row.name}
                    />
                    {row.name}
                    {isImplied && " (inside a ticked folder)"}
                  </FolderRow>
                );
              })}
            </FolderPicker>
            <PickerNote>
              {sending.length === 1
                ? "1 folder on this link."
                : `${sending.length} folders on this link.`}
            </PickerNote>

            <SubLabel>Security</SubLabel>
            <PasswordRow>
              <Label small>Set Password (Optional)</Label>
              <GenerateButton onClick={generatePassword}>
                <ArrowsClockwise size={16} color="#0C53BB" />
                Generate
              </GenerateButton>
            </PasswordRow>
            <PasswordWrapper>
              <PasswordInput
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={Boolean(errors.password)}
              />
              {/* Both controls, not one or the other. A password you have
                  typed or generated is the one you most need to read back
                  before sending it on. */}
              <PasswordIcons>
                <span
                  role="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <Eye size={16} /> : <EyeSlash size={16} />}
                </span>
                {password && (
                  <span
                    role="button"
                    aria-label="Copy password"
                    onClick={() => copyToClipboard(password, "Password")}
                  >
                    <Copy size={16} color="#0C53BB" />
                  </span>
                )}
              </PasswordIcons>
            </PasswordWrapper>
            {errors.password && (
              <ErrorText style={{ alignItems: "flex-start" }}>
                <ErrorIconWrapper>
                  <ErrorIcon />
                </ErrorIconWrapper>
                {errors.password}
              </ErrorText>
            )}

            <Label small>Link expires on</Label>
            <>
              <DatePickerDropdownStyles />
              <StyledDatePicker
                format={dateFormatList}
                getPopupContainer={() => document.querySelector(".share-modal")}
                className={errors.expiryDate ? "error" : ""}
                onChange={(date) => setExpiryDate(date)}
                allowClear={false}
                inputReadOnly
                placeholder="Due Date"
                value={expiryDate}
                disabledDate={(current) => {
                  const today = dayjs().startOf("day");
                  // 90 days, matching what the folder-share endpoint
                  // accepts. Matthew's call on 9/9: a doctor's opinion
                  // routinely takes longer than a month, and a link that
                  // dies mid-opinion means the request starts again.
                  const maxDate = dayjs().add(MAX_EXPIRY_DAYS, "day").endOf("day");
                  return current && (current < today || current > maxDate);
                }}
                error={Boolean(errors.expiryDate)}
              />
              {errors.expiryDate && (
                <ErrorText>
                  <ErrorIcon />
                  {errors.expiryDate}
                </ErrorText>
              )}
            </>

            {liveLinks.length > 0 && (
              <AccessListSection>
                <PasswordRow>
                  <SubLabel>Links to this file</SubLabel>
                  <RevokeLink onClick={() => setShowRevokeModal(true)}>
                    Revoke Access
                  </RevokeLink>
                </PasswordRow>
                {liveLinks.map((link) => (
                  <LinkRow key={link.id}>
                    <LinkMeta>
                      <strong>
                        {link.folders?.map((f) => f.name).join(", ")}
                      </strong>
                      {describeLink(link)}
                      {" · expires "}
                      {dayjs(link.expires_at).format("MM/DD/YYYY")}
                      {link.has_password && " · password set"}
                    </LinkMeta>
                    <GenerateButton
                      onClick={() => copyToClipboard(link.share_link, "Link")}
                    >
                      <Copy size={16} color="#0C53BB" />
                      Copy Link
                    </GenerateButton>
                  </LinkRow>
                ))}
              </AccessListSection>
            )}

            <HorizondalLine />
            <ButtonGroup>
              <CancelButton onClick={onClose}>
                {createdLink ? "Done" : "Cancel"}
              </CancelButton>
              <ShareButton
                onClick={handleSubmit}
                disabled={!(sending.length > 0 && expiryDate)}
              >
                {createdLink ? "Create another link" : "Create link"}
              </ShareButton>
            </ButtonGroup>
          </ModalContent>
          <BackButton>
            <X size={20} onClick={onClose} />
          </BackButton>
        </ModalContainer>
      ) : (
        <MessageModal
          open={true}
          onClose={onClose}
          title={"Warning: External Share Disabled"}
          message={
            "External Share Email notifications are currently disabled. Please contact your admin."
          }
          buttonText={"Close"}
          icon={<WarningIcon />}
          onClick={onClose}
          variant="warning"
        />
      )}
      <ConfirmModal
        isOpen={showRevokeModal}
        title="Revoke Access?"
        message={
          fromFile
            ? "This will disable every link on this file for all recipients."
            : "This will disable every link to this folder for all recipients."
        }
        onCancel={() => setShowRevokeModal(false)}
        onConfirm={handleRevoke}
      />
      {isLoading && <Loader message={"Creating link..."} />}
    </Overlay>
  );
};

ShareFolderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // One of the two: the folder the screen was opened from, or the client
  // file when opened from the top of it.
  folder_id: PropTypes.string,
  matter_id: PropTypes.string,
  // Shown in the heading when opened from a file (a folder names itself).
  title: PropTypes.string,
  handleSnackbar: PropTypes.func,
};

export default ShareFolderModal;
